/*----------------------------------
- CATALOG: DOMAINS PAGE COPY
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr  ADR-0028
 *
 * The words of the `/domains*` page around its table: the title row's controls, the filter rail and its sheet, the
 * closing block, the film and Ask AI (docs/mockups/website-domains.html, batch 7 part 3). The table's own words are
 * in `publicTableColumns.ts` and `publicPreviewCopy.ts`, the drawer's in `publicRowDrawer.ts`.
 *
 * No figure is typed here: the counts come from the search, the plan facts from `PUBLIC_PLAN_FACTS` and
 * `REFUND_GUARANTEE_DAYS` where the closing paragraph is built.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { REFUND_GUARANTEE_DAYS } from '@uniquedomains/components/pricing/catalogs/refundPromise';

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

/** The H1 when the search carries no SEO title and no summary (the shipped fallback). */
export const DOMAINS_PAGE_TITLE_FALLBACK = 'One-Word Domains';

/** The title row's controls. Every one opens authentication (ADR-0029, ADR-0035, ADR-0050). */
export const DOMAINS_TITLE_ACTIONS_COPY = {
    filters: 'Filters',
    sortPrefix: 'Sort',
    download: 'Download',
    createRadar: 'Create radar',
} as const;

/** The GitHub visit banner's persona tag ("Investor · Full Access"). */
export const DOMAINS_GITHUB_PERSONA_LABELS = { investor: 'Investor', founder: 'Founder' } as const;

/** The filter rail and its Filters sheet below 1280px. */
export const DOMAINS_RAIL_COPY = {
    /** Beside the rail's column and as the landmark's name. */
    railLabel: 'Filters',
    /** A filter with no rule on the search on screen. */
    any: 'Any',
    /** A filter with a rule the summary formatter cannot word (the shipped fallback). */
    active: 'Active',
    /** Spoken after a locked filter's name: its head opens the plan's wall instead of the filter. */
    locked: 'Locked',
    rangeMinimum: 'Minimum',
    rangeMaximum: 'Maximum',
    rangeJoin: 'to',
    /** The range's read-only track, named after its filter ("Price range"). */
    rangeTrack: (label: string) => `${label} range`,
    patternDirection: 'Pattern direction',
    patternStartsWith: 'Starts with',
    patternEndsWith: 'Ends with',
    patternPlaceholder: 'Enter pattern',
    patternLabel: 'Pattern',
    patternApply: 'Apply',
    sheetTitle: 'Filters',
    sheetLabel: 'Browse filters',
    /* Done rather than a count of results: nothing in this rail changes the rows (ADR-0035), so there is no count
       to promise. */
    sheetDone: 'Done',
} as const;

/** The region holding the table (the table itself carries the same name, which the journeys read). */
export const DOMAINS_RESULTS_LABEL = 'Domain results';

/** "Browse more sectors": the four other sectors, on a sector page only. */
export const DOMAINS_RELATED_SECTORS_LABEL = 'Browse more sectors';

/**
 * The closing block (the Home hero's two-line shape). Cold traffic arrives looking for a "one word domains database",
 * so the copy says the database is the entry point, not the product, and claims only what Full Access does.
 */
export const DOMAINS_CLOSING_COPY = {
    headlineLead: 'You wanted a list of',
    headlineMark: 'one-word domains',
    headlineEnd: '?',
    answerLead: 'This one',
    answerMark: 'vets and tracks them',
    answerEnd: 'for you.',
    /* "asking prices", not "live asks": the scan samples one compatible registrar per name, so no figure on this page
       is live (ADR-0032). "Estimated value", not "resale range": the home-grown estimators were retired on
       2026-07-25. No free plan since ADR-0083: the paragraph ends on the price and the refund. */
    paragraph: (totalCountLabel: string | null) =>
        `Search ${totalCountLabel ? `${totalCountLabel} names` : 'every name'} by prompt or filters. Price, demand, history, trademark risk and estimated value on every one. Full Access is $${PUBLIC_PLAN_FACTS.member.price} a month, money back within ${REFUND_GUARANTEE_DAYS} days.`,
    /* Sentence case as the mock writes the persona pair (mock head comment, "Copy changed from shipped"); the shipped
       pair read "I'm an Investor" / "I'm a Founder". */
    personaCtas: { investor: "I'm an investor", founder: "I'm a founder" },
} as const;

/** The closing figures, value over label. */
export const DOMAINS_STATS_COPY = {
    label: 'One-word domain market statistics',
    tracked: 'One-word domains tracked',
    medianAsk: 'Median ask',
    tlds: 'TLDs tracked',
} as const;

/** The product film's poster and its play control (the video's own copy is `SCREENER_FILM_COPY`). */
export const DOMAINS_FILM_COPY = {
    playTitle: 'Plays the 29-second walkthrough of the product, then pauses on its last frame',
} as const;
